<template>
  <div>
    <!-- full-height side panel — same pattern as the property detail drawer -->
    <v-navigation-drawer
      v-model="show"
      right
      temporary
      fixed
      :width="$vuetify.breakpoint.smAndDown ? $vuetify.breakpoint.width : 720"
      class="iqd-drawer"
    >
      <div class="iqd-main d-flex flex-column">
          <!-- HEADER -->
          <div class="iqd-head">
            <div class="iqd-head__left">
              <div class="iqd-head__title">{{ inquiry.name || "Consulta" }}</div>
              <div class="iqd-head__sub">
                <hero-icon :icon="subjectIcon" :size="13" />
                <span>{{ subject }}</span>
              </div>
              <div class="iqd-head__meta">
                <span
                  v-if="inquiry.leadTier"
                  class="iqd-lead"
                  :class="'iqd-lead--' + inquiry.leadTier"
                  title="Temperatura del lead"
                >{{ tierLabel(inquiry.leadTier) }}<template v-if="inquiry.leadScore != null"> · {{ inquiry.leadScore }}</template></span>
                <span class="iqd-badge" :class="'iqd-badge--' + (state || inquiry.state)">{{ stateLabel }}</span>
                <span v-if="(state || inquiry.state) === 'lost' && inquiry.lostReason" class="iqd-badge iqd-badge--lost">{{ lostReasonLabel(inquiry.lostReason) }}</span>
                <span class="iqd-origin" :class="'iqd-origin--' + origin.key">{{ origin.label }}</span>
                <span class="iqd-head__date" :title="fullDate">{{ waiting }}</span>
              </div>
            </div>

            <div class="iqd-head__actions">
              <button
                v-if="currentUser.displayRoles.includes('admin')"
                type="button"
                class="iqd-btn iqd-btn--danger"
                @click="dialog = true"
              >
                <hero-icon :icon="TrashIcon" :size="15" />
                <span>Eliminar</span>
              </button>

              <button
                type="button"
                class="iqd-iconbtn"
                aria-label="Cerrar"
                @click="show = false"
              >
                <hero-icon :icon="XMarkIcon" :size="18" />
              </button>
            </div>
          </div>

          <div class="iqd-divider"></div>

          <!-- TOOLBAR: assigned-to menu + state select (visible on both tabs) -->
          <div class="iqd-toolbar">
              <v-menu offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <button
                    type="button"
                    class="iqd-assign"
                    v-on="on"
                    v-bind="attrs"
                  >
                    <hero-icon :icon="UserIcon" :size="14" />
                    <span class="iqd-assign__label">Asignada a:</span>
                    <span class="iqd-assign__value">
                      {{ inquiry.user ? inquiry.user.email : "sin asignar" }}
                    </span>
                    <hero-icon :icon="ChevronDown" :size="14" />
                  </button>
                </template>

                <v-list dense>
                  <v-list-item
                    link
                    v-for="mail in [
                      'inmobiliariasartor@yahoo.com.ar',
                      'buseghinv@gmail.com',
                      'lorenaberlanda@yahoo.com.ar',
                      'franco_sartor@hotmail.com',
                      'ramirosartor@hotmail.com'
                    ]"
                    :key="mail"
                    @click="assign(mail)"
                  >
                    <v-list-item-title>{{ mail }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>

              <!-- próxima acción: the follow-up that keeps the lead alive -->
              <v-menu v-model="nextMenu" offset-y :close-on-content-click="false">
                <template v-slot:activator="{ on, attrs }">
                  <button
                    type="button"
                    class="iqd-next"
                    :class="{ 'iqd-next--overdue': nextActionOverdue, 'iqd-next--set': inquiry.nextActionAt }"
                    v-on="on"
                    v-bind="attrs"
                    @click="openNextMenu"
                  >{{ nextActionLabel }}</button>
                </template>
                <div class="iqd-nextmenu">
                  <div class="iqd-nextmenu__title">Próxima acción</div>
                  <input v-model="nextDate" type="date" class="iqd-nextmenu__date" />
                  <input v-model="nextNote" type="text" maxlength="140" placeholder="Nota (llamar, enviar tasación...)" class="iqd-nextmenu__note" />
                  <div class="iqd-nextmenu__actions">
                    <button v-if="inquiry.nextActionAt" type="button" class="iqd-nextmenu__clear" @click="saveNextAction(true)">Quitar</button>
                    <button type="button" class="iqd-nextmenu__save" :disabled="!nextDate" @click="saveNextAction()">Guardar</button>
                  </div>
                </div>
              </v-menu>

              <v-select
                v-model="state"
                :items="states"
                label="Estado"
                outlined
                dense
                hide-details
                class="iqd-state ml-auto"
                @change="updateState"
              ></v-select>
          </div>

          <!-- TABS: conversation (client-visible) / internal notes / context -->
          <v-tabs v-model="tab" height="42" color="#b52118" class="iqd-tabs">
            <v-tab>
              Conversación
              <span v-if="clientThread.length" class="iqd-tabcount">{{ clientThread.length }}</span>
            </v-tab>
            <v-tab>
              Notas
              <span v-if="notes.length" class="iqd-tabcount">{{ notes.length }}</span>
            </v-tab>
            <v-tab>Detalle</v-tab>
          </v-tabs>
          <div class="iqd-divider"></div>

          <!-- CONVERSACIÓN -->
          <!-- pinned summary of what they're asking for — kicks off the reply
               without switching to Detalle (chips open the property preview) -->
          <div v-if="properties.length" v-show="tab === 0" class="iqd-pkgbar">
            <span class="iqd-pkgbar__label">Consulta por</span>
            <div class="iqd-pkgbar__chips">
              <property-preview
                v-for="p in properties"
                :key="'chip-' + p.id"
                :property="p"
                chip
              ></property-preview>
            </div>
          </div>

          <div v-show="tab === 0" ref="thread" class="iqd-body iqd-body--thread">
            <!-- linked live chat (born from / tied to a conversation) -->
            <button
              v-if="inquiry.agentConversation"
              type="button"
              class="iqd-chat"
              @click="openChat"
            >
              <span class="iqd-chat__icon">
                <hero-icon :icon="ChatIcon" :size="17" />
              </span>
              <span class="iqd-chat__text">
                <span class="iqd-chat__title">
                  Chat vinculado
                  <span v-if="chatUnread" class="iqd-chat__unread">{{ chatUnread }} sin leer</span>
                </span>
                <span class="iqd-chat__meta">{{ chatMeta }}</span>
              </span>
              <span class="iqd-chat__go">
                Abrir en Vivo <hero-icon :icon="ArrowIcon" :size="14" />
              </span>
            </button>

            <!-- the client-visible conversation: original message + the thread -->
            <div v-if="!clientThread.length" class="iqd-comments__empty">
              Aún no hay mensajes en esta consulta.
            </div>
            <div
              v-for="comment in clientThread"
              :key="comment.id"
              class="iqd-bubble"
              :class="{ 'iqd-bubble--client': comment.fromClient }"
            >
              <div class="iqd-bubble__head">
                <span class="iqd-bubble__author">
                  {{ commentAuthor(comment) }}
                  <span
                    v-if="comment.original"
                    class="iqd-bubble__tag iqd-bubble__tag--client"
                  >Consulta original</span>
                  <span
                    v-else-if="comment.fromClient"
                    class="iqd-bubble__tag iqd-bubble__tag--client"
                  >Cliente</span>
                  <span v-else class="iqd-bubble__tag">Enviado al cliente</span>
                </span>
                <span class="iqd-bubble__date">
                  {{ commentDate(comment.createdAt) }}
                </span>
              </div>
              <div class="iqd-bubble__text">{{ comment.text }}</div>
            </div>
          </div>

          <!-- NOTAS (internal — the client never sees these) -->
          <div v-show="tab === 1" ref="notes" class="iqd-body iqd-body--thread">
            <div v-if="!notes.length" class="iqd-comments__empty">
              Sin notas internas todavía.
            </div>
            <div v-for="note in notes" :key="note.id" class="iqd-bubble">
              <div class="iqd-bubble__head">
                <span class="iqd-bubble__author">{{ commentAuthor(note) }}</span>
                <span class="iqd-bubble__date">{{ commentDate(note.createdAt) }}</span>
              </div>
              <div class="iqd-bubble__text">{{ note.text }}</div>
            </div>
          </div>

          <!-- DETALLE -->
          <div v-show="tab === 2" class="iqd-body">
            <!-- contact + properties -->
            <div class="iqd-cards">
              <contact-preview :inquiry="inquiry">
                <template v-slot:actions>
                  <modal
                    v-if="!inquiry.contact"
                    ref="modal"
                    text="Agregar a agenda"
                    solo
                    color="primary"
                    button_text
                  >
                    <new-contact
                      :data="inquiry"
                      list
                      @close="$refs.modal.dialog = false"
                      @created="onCreate"
                    ></new-contact>
                  </modal>
                </template>
              </contact-preview>

              <!-- the full multi-property package (or the single property) -->
              <div v-if="properties.length" class="iqd-pkg">
                <div class="iqd-seclabel">
                  {{ properties.length > 1 ? "Propiedades del paquete (" + properties.length + ")" : "Propiedad" }}
                </div>
                <property-preview
                  v-for="p in properties"
                  :key="p.id"
                  :property="p"
                ></property-preview>
              </div>

              <!-- recommendations (inferred from the inquiry) -->
              <recommendations :inquiry-id="inquiry.id"></recommendations>
            </div>
          </div>

          <!-- COMPOSER: conversation tab replies to the CLIENT, notes tab stays internal -->
          <div v-show="tab === 0 || tab === 1" class="iqd-composerwrap">
            <div v-if="tab === 0" class="iqd-composerhint iqd-composerhint--client">
              ✉ El cliente ve esta respuesta en el sitio y la recibe por email.
            </div>
            <div v-else class="iqd-composerhint">
              Nota interna — solo la ve el equipo, al cliente nunca le llega.
            </div>
            <div class="iqd-composer" :class="{ 'iqd-composer--client': tab === 0 }">
              <v-textarea
                v-model="newComment"
                outlined
                dense
                rows="2"
                auto-grow
                hide-details
                :placeholder="tab === 0 ? 'Escribí la respuesta para el cliente...' : 'Escribí una nota interna...'"
                class="iqd-textarea"
              ></v-textarea>

              <button
                type="button"
                class="iqd-send"
                :disabled="!newComment"
                aria-label="Enviar comentario"
                @click="send"
              >
                <hero-icon :icon="PaperAirplaneIcon" :size="18" />
              </button>
            </div>
          </div>
      </div>
    </v-navigation-drawer>

    <!-- LOST REASON (state → Perdida) -->
    <v-dialog v-model="lostDialog" width="380" content-class="inquiry-dialog" @click:outside="cancelLost">
      <v-card flat class="iqd-lost">
        <div class="iqd-lost__title">¿Por qué se perdió?</div>
        <button
          v-for="r in lostReasons"
          :key="r.value"
          type="button"
          class="iqd-lost__opt"
          @click="confirmLost(r.value)"
        >{{ r.label }}</button>
        <button type="button" class="iqd-lost__cancel" @click="cancelLost">Cancelar</button>
      </v-card>
    </v-dialog>

    <!-- DELETE CONFIRM (admin only) -->
    <v-dialog
      v-if="currentUser.displayRoles.includes('admin')"
      v-model="dialog"
      width="460"
      content-class="inquiry-dialog"
    >
      <v-card flat class="iqd-confirm">
        <div class="iqd-confirm__head">
          <div class="iqd-confirm__title">Eliminar consulta</div>
          <button
            type="button"
            class="iqd-iconbtn"
            aria-label="Cerrar"
            @click="dialog = false"
          >
            <hero-icon :icon="XMarkIcon" :size="18" />
          </button>
        </div>

        <div class="iqd-divider"></div>

        <div class="iqd-confirm__body">
          ¿Estás seguro de que deseas eliminar la consulta? Esta acción
          <strong>no se puede deshacer</strong>.
        </div>

        <div class="iqd-confirm__actions">
          <button
            type="button"
            class="iqd-btn iqd-btn--ghost"
            @click="dialog = false"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="iqd-btn iqd-btn--danger"
            @click="deleteInquiry"
          >
            <hero-icon :icon="TrashIcon" :size="15" />
            <span>Eliminar</span>
          </button>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { Comments } from "@/graphql/queries/inquiries";
import { subscribeInquiries } from "@/services/inquiriesCable";
import {
  CreateComment,
  DeleteInquiry,
  AssignInquiry,
  UpdateInquiryContact,
  UpdateInquiryState,
  SetInquiryNextAction
} from "@/graphql/mutations/inquiries";
import { FUNNEL, LOST_REASONS, lostReasonLabel } from "@/utils/funnel";

import Modal from "@/components/modals/Default";
import NewContact from "@/components/directories/New";
import HeroIcon from "@/components/shared/HeroIcon";

import ChevronDown from "heroicons/24/outline/chevron-down.svg";
import XMarkIcon from "heroicons/24/outline/x-mark.svg";
import TrashIcon from "heroicons/24/outline/trash.svg";
import UserIcon from "heroicons/24/outline/user.svg";
import PaperAirplaneIcon from "heroicons/24/outline/paper-airplane.svg";
import ChatIcon from "heroicons/24/outline/chat-bubble-left-right.svg";
import ArrowIcon from "heroicons/24/outline/arrow-up-right.svg";
import HomeIcon from "heroicons/24/outline/home.svg";
import MapPinIcon from "heroicons/24/outline/map-pin.svg";
import Squares2X2Icon from "heroicons/24/outline/squares-2x2.svg";

import ContactPreview from "@/components/inquiries/ContactPreview";
import PropertyPreview from "@/components/inquiries/PropertyPreview";
import Recommendations from "@/components/inquiries/Recommendations";

export default {
  data: () => ({
    show: false,
    dialog: false,
    tab: 0,
    comments: [],
    newComment: null,
    state: null,
    lostDialog: false,
    lostReasons: LOST_REASONS,
    nextMenu: false,
    nextDate: "",
    nextNote: "",

    ChevronDown,
    XMarkIcon,
    TrashIcon,
    UserIcon,
    PaperAirplaneIcon,
    ChatIcon,
    ArrowIcon,
    HomeIcon,
    MapPinIcon,
    Squares2X2Icon,
    tierLabels: { hot: "Caliente", warm: "Tibio", cold: "Frío" }
  }),

  props: {
    inquiry: {
      required: true,
      type: Object
    }
  },

  computed: {
    ...mapGetters(["currentUser"]),

    states() {
      // the funnel, in board order (see utils/funnel.js)
      return FUNNEL.map(s => ({ text: s.label, value: s.state }));
    },

    nextActionLabel() {
      const at = this.inquiry.nextActionAt;
      if (!at) return "Seguimiento";
      const d = new Date(at);
      const today = new Date().toDateString() === d.toDateString();
      return "⏰ " + (today ? "hoy" : d.toLocaleDateString("es-AR", { day: "numeric", month: "short" }));
    },

    nextActionOverdue() {
      const at = this.inquiry.nextActionAt;
      return at && new Date(at).getTime() < Date.now() && new Date(at).toDateString() !== new Date().toDateString();
    },

    // the full package — falls back to the primary property
    properties() {
      const list = this.inquiry.properties || [];
      if (list.length) return list;
      return this.inquiry.property ? [this.inquiry.property] : [];
    },

    isPackage() {
      return this.properties.length > 1;
    },

    isGlobal() {
      return this.properties.length === 0;
    },

    subject() {
      if (this.isPackage) return `Paquete · ${this.properties.length} propiedades`;
      const p = this.properties[0];
      if (p) return p.fullAddress;
      const base =
        this.inquiry.topic ||
        (this.inquiry.investmentType
          ? this.$t("investmentTypes." + this.inquiry.investmentType)
          : "");
      const loc = this.inquiry.location;
      if (base && loc) return base + " · " + loc;
      return base || loc || "Consulta general";
    },

    subjectIcon() {
      if (this.isPackage) return Squares2X2Icon;
      return this.isGlobal ? MapPinIcon : HomeIcon;
    },

    origin() {
      if (this.inquiry.manual) return { key: "manual", label: "Manual" };
      if (this.inquiry.agentConversation) return { key: "chat", label: "Chat" };
      return { key: "web", label: "Web" };
    },

    chatUnread() {
      const c = this.inquiry.agentConversation;
      return (c && c.agentUnread) || 0;
    },

    chatMeta() {
      const c = this.inquiry.agentConversation;
      if (!c) return "";
      if (c.lastMessagePreview) return c.lastMessagePreview;
      if (c.lastMessageAt) return "Última actividad " + this.timeAgo(c.lastMessageAt);
      return c.status === "closed" ? "Conversación finalizada" : "Conversación abierta";
    },

    headerDate() {
      return new Date(this.inquiry.createdAt).toLocaleDateString("es-AR", {
        day: "numeric",
        month: "short",
        year: "numeric"
      });
    },

    waiting() {
      return this.timeAgo(this.inquiry.createdAtTime || this.inquiry.createdAt);
    },

    fullDate() {
      const iso = this.inquiry.createdAtTime || this.inquiry.createdAt;
      return iso
        ? new Date(iso).toLocaleString("es-AR", {
            day: "numeric",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit"
          })
        : "";
    },

    stateLabel() {
      const current = this.state || this.inquiry.state;
      const match = this.states.find(s => s.value === current);
      return match ? match.text : "";
    },

    // the client-visible conversation: the original inquiry message as the first
    // bubble, then only the comments the client can see
    clientThread() {
      const head = this.inquiry.message
        ? [{
            id: "original",
            text: this.inquiry.message,
            fromClient: true,
            original: true,
            createdAt: this.inquiry.createdAtTime || this.inquiry.createdAt
          }]
        : [];
      return head.concat(this.comments.filter(c => c.visibleToClient));
    },

    // internal notes (everything pre-thread lives here too)
    notes() {
      return this.comments.filter(c => !c.visibleToClient);
    }
  },

  watch: {
    // subscribe only while the drawer is open (a Detail mounts per list card)
    show(open) {
      if (open) {
        this.fetchComments();
        this.scrollThread();
        if (!this._sub) {
          this._sub = subscribeInquiries(e => {
            if (String(e.inquiryId) !== String(this.inquiry.id)) return;
            if (e.type === "comment") this.fetchComments();
            if (e.type === "state") this.state = e.state; // another seat moved the funnel
          });
        }
      } else if (this._sub) {
        this._sub.unsubscribe();
        this._sub = null;
      }
    },
    tab(t) {
      if (t === 0 || t === 1) this.scrollThread();
    }
  },

  created() {
    this.fetchComments();
    this.state = this.inquiry.state;
  },

  beforeDestroy() {
    if (this._sub) this._sub.unsubscribe();
  },

  methods: {
    lostReasonLabel,

    tierLabel(t) {
      return this.tierLabels[t] || t;
    },

    timeAgo(iso) {
      if (!iso) return "";
      const diff = Math.max(0, Date.now() - new Date(iso).getTime());
      const m = Math.floor(diff / 60000);
      if (m < 1) return "recién";
      if (m < 60) return `hace ${m} min`;
      const h = Math.floor(m / 60);
      if (h < 24) return `hace ${h} h`;
      const d = Math.floor(h / 24);
      if (d < 30) return `hace ${d} día${d === 1 ? "" : "s"}`;
      const mo = Math.floor(d / 30);
      if (mo < 12) return `hace ${mo} mes${mo === 1 ? "" : "es"}`;
      const y = Math.floor(d / 365);
      return `hace ${y} año${y === 1 ? "" : "s"}`;
    },

    openChat() {
      const c = this.inquiry.agentConversation;
      if (!c) return;
      this.show = false;
      this.$router
        .push({ name: "Live", query: { c: c.id } })
        .catch(() => {});
    },

    commentDate(date) {
      return new Date(date).toLocaleDateString("es-AR", {
        day: "numeric",
        month: "short",
        year: "numeric"
      });
    },

    commentAuthor(comment) {
      if (comment.fromClient) return this.inquiry.name || "Cliente";
      return comment.user.id == this.currentUser.id ? "Tú" : comment.user.email;
    },

    scrollThread() {
      this.$nextTick(() => {
        const el = this.tab === 1 ? this.$refs.notes : this.$refs.thread;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },

    fetchComments() {
      this.$apollo
        .query({
          query: Comments,
          variables: { id: this.inquiry.id },
          fetchPolicy: "no-cache"
        })
        .then(res => {
          this.comments = res.data.comments;
          this.scrollThread();
        });
    },

    send() {
      this.$apollo
        .mutate({
          mutation: CreateComment,
          variables: {
            input: {
              attributes: {
                text: this.newComment,
                inquiryId: this.inquiry.id,
                // conversation tab replies to the client; notes tab stays internal
                visibleToClient: this.tab === 0
              }
            }
          }
        })
        .then(() => {
          this.newComment = null;
          this.fetchComments();
        });
    },

    assign(mail) {
      this.$apollo
        .mutate({
          mutation: AssignInquiry,
          variables: { input: { id: this.inquiry.id, email: mail } }
        })
        .then(() => {
          this.$emit("reload");
          this.inquiry.user = { email: mail };
        });
    },

    updateContact(id) {
      this.$apollo
        .mutate({
          mutation: UpdateInquiryContact,
          variables: { input: { id: this.inquiry.id, contactId: id } }
        })
        .then(() => {
          this.$emit("reload");
          this.$forceUpdate();
        });
    },

    updateState() {
      // Perdida needs a reason before persisting (cancel reverts the select)
      if (this.state === "lost") {
        this.lostDialog = true;
        return;
      }
      this.persistState();
    },

    persistState(lostReason = null) {
      this.$apollo
        .mutate({
          mutation: UpdateInquiryState,
          variables: { input: { id: this.inquiry.id, state: this.state, lostReason } }
        })
        .then(() => {
          this.inquiry.state = this.state;
          this.inquiry.lostReason = lostReason;
          this.$emit("reload");
          this.$forceUpdate();
        });
    },

    confirmLost(reason) {
      this.lostDialog = false;
      this.persistState(reason);
    },

    cancelLost() {
      this.lostDialog = false;
      this.state = this.inquiry.state; // revert the select
    },

    openNextMenu() {
      const at = this.inquiry.nextActionAt;
      this.nextDate = at ? new Date(at).toISOString().slice(0, 10) : "";
      this.nextNote = this.inquiry.nextActionNote || "";
    },

    saveNextAction(clear = false) {
      const at = !clear && this.nextDate ? new Date(this.nextDate + "T09:00:00").toISOString() : null;
      this.$apollo
        .mutate({
          mutation: SetInquiryNextAction,
          variables: { input: { id: this.inquiry.id, at, note: at ? this.nextNote : null } }
        })
        .then(() => {
          this.inquiry.nextActionAt = at;
          this.inquiry.nextActionNote = at ? this.nextNote : null;
          this.nextMenu = false;
          this.$emit("reload");
          this.$forceUpdate();
        });
    },

    deleteInquiry() {
      this.$apollo
        .mutate({
          mutation: DeleteInquiry,
          variables: { input: { id: this.inquiry.id } }
        })
        .then(() => {
          this.show = false;
          this.$emit("reload");
          this.$forceUpdate();
        });
    },

    onCreate(contact) {
      this.updateContact(contact.id);
      this.$refs.modal.dialog = false;
    }
  },

  components: {
    Modal,
    NewContact,
    HeroIcon,
    ContactPreview,
    PropertyPreview,
    Recommendations
  }
};
</script>

<style scoped>
/* main panel — full-height side drawer */
.iqd-main {
  width: 100%;
  height: 100%;
  background: #fff;
  display: flex;
  flex-direction: column;
}

/* tabs */
.iqd-tabs {
  flex: none;
}
.iqd-tabs >>> .v-tab {
  text-transform: none;
  letter-spacing: 0;
  font-size: 13.5px;
  font-weight: 600;
}
.iqd-tabcount {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 7px;
  min-width: 18px;
  height: 18px;
  border-radius: 999px;
  background: #f4f1ea;
  color: #56504a;
  font-size: 10.5px;
  font-weight: 700;
  padding: 0 5px;
}
.v-tab--active .iqd-tabcount { background: #fdeed3; color: #92400e; }

/* conversation tab */
.iqd-body--thread { background: #faf8f4; }

/* pinned "Consulta por" property summary */
.iqd-pkgbar {
  flex: none;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 22px;
  background: #faf8f4;
  border-bottom: 1px solid #ece7de;
}
.iqd-pkgbar__label {
  flex: none;
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #a39a8c;
  font-weight: 600;
}
.iqd-pkgbar__chips {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  min-width: 0;
  padding-bottom: 2px;
}

/* ---- HEADER ---- */
.iqd-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 22px 16px;
}
.iqd-head__left { min-width: 0; }
.iqd-head__title {
  font-size: 19px;
  font-weight: 700;
  color: #1a1814;
  line-height: 1.25;
}
.iqd-head__sub {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  font-size: 13px;
  color: #756f64;
}
.iqd-head__sub .hero-icon { color: #a39a8c; flex: none; }
.iqd-head__meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}
.iqd-head__date { font-size: 12.5px; color: #a39a8c; }
.iqd-lead {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: .03em;
}
.iqd-lead--hot { background: #fdecea; color: #b52118; }
.iqd-lead--warm { background: #fdeecf; color: #8a5a00; }
.iqd-lead--cold { background: #eef1f4; color: #6a727b; }
.iqd-origin {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border-radius: 999px;
  padding: 2px 8px;
}
.iqd-origin--manual { background: #ece9e3; color: #6a645a; }
.iqd-origin--chat { background: #e1ecff; color: #2353b8; }
.iqd-origin--web { background: #f1ece3; color: #8a7d63; }
.iqd-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 10px;
  border-radius: 999px;
  line-height: 1.6;
}
.iqd-badge--pending { background: #fdeecf; color: #8a5a00; }
.iqd-badge--ongoing { background: #e1ecff; color: #2353b8; }
.iqd-badge--on_hold { background: #efe3f7; color: #6a3a8f; }
.iqd-badge--watching { background: #efe3f7; color: #6a3a8f; }
.iqd-badge--visit { background: #e0f4f8; color: #0e7490; }
.iqd-badge--negotiation { background: #fdeed3; color: #92400e; }
.iqd-badge--success { background: #e3f3e8; color: #2c6e49; }
.iqd-badge--lost { background: #ece9e3; color: #6a645a; }

/* próxima acción */
.iqd-next {
  font-size: 12px;
  font-weight: 600;
  color: #6a645a;
  background: #faf8f4;
  border: 1px solid #e7e2d9;
  border-radius: 999px;
  padding: 7px 14px;
  cursor: pointer;
  white-space: nowrap;
}
.iqd-next--set { color: #1a1814; }
.iqd-next--overdue { color: #b52118; border-color: #f3d5d1; background: #fdf0ee; }
.iqd-nextmenu { background: #fff; padding: 14px; width: 260px; }
.iqd-nextmenu__title { font-size: 12px; font-weight: 700; color: #1a1814; margin-bottom: 9px; }
.iqd-nextmenu__date,
.iqd-nextmenu__note {
  display: block;
  width: 100%;
  border: 1px solid #ddd5c8;
  border-radius: 8px;
  padding: 7px 10px;
  font: inherit;
  font-size: 13px;
  margin-bottom: 8px;
}
.iqd-nextmenu__actions { display: flex; justify-content: flex-end; gap: 8px; }
.iqd-nextmenu__clear { background: none; border: none; color: #b52118; font-size: 12.5px; cursor: pointer; padding: 6px 8px; }
.iqd-nextmenu__save {
  background: #1a1814;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 7px 16px;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
}
.iqd-nextmenu__save:disabled { opacity: 0.4; cursor: default; }

/* lost reason dialog */
.iqd-lost { padding: 18px 18px 14px; }
.iqd-lost__title { font-size: 15px; font-weight: 700; color: #1a1814; margin-bottom: 12px; }
.iqd-lost__opt {
  display: block;
  width: 100%;
  text-align: left;
  background: #faf8f4;
  border: 1px solid #ece7de;
  border-radius: 9px;
  padding: 9px 12px;
  margin-bottom: 6px;
  font-size: 13px;
  color: #1a1814;
  cursor: pointer;
}
.iqd-lost__opt:hover { border-color: #b52118; }
.iqd-lost__cancel {
  display: block;
  width: 100%;
  background: none;
  border: none;
  padding: 8px;
  font-size: 12.5px;
  color: #a39a8c;
  cursor: pointer;
}

.iqd-head__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: none;
}

/* buttons */
.iqd-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 10px;
  border: 1px solid transparent;
  padding: 7px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}
.iqd-btn--danger {
  background: #fff;
  border-color: #e7c9c5;
  color: #b52118;
}
.iqd-btn--danger:hover {
  background: #b52118;
  border-color: #b52118;
  color: #fff;
}
.iqd-btn--ghost {
  background: #fff;
  border-color: #dcd4c6;
  color: #1a1814;
}
.iqd-btn--ghost:hover { border-color: #c2b9a8; }

.iqd-iconbtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 9px;
  border: 1px solid transparent;
  background: transparent;
  color: #756f64;
  cursor: pointer;
  transition: all 0.15s ease;
}
.iqd-iconbtn:hover {
  background: #f1ece3;
  color: #1a1814;
}

.iqd-divider { height: 1px; background: #ece7de; }

/* ---- BODY ---- */
.iqd-body {
  flex-grow: 1;
  overflow: auto;
  scrollbar-gutter: stable;
  padding: 18px 22px;
}

/* toolbar */
.iqd-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 22px;
}
.iqd-assign {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: #faf8f4;
  border: 1px solid #ece7de;
  border-radius: 10px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 12.5px;
  color: #756f64;
  transition: border-color 0.15s ease;
  min-width: 0;
}
.iqd-assign:hover { border-color: #dcd4c6; }
.iqd-assign .hero-icon { color: #a39a8c; flex: none; }
.iqd-assign__label { font-weight: 600; color: #756f64; white-space: nowrap; }
.iqd-assign__value {
  font-weight: 600;
  color: #1a1814;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.iqd-state { max-width: 200px; }

/* linked live chat strip */
.iqd-chat {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  text-align: left;
  background: #f4f7fd;
  border: 1px solid #d9e4fb;
  border-radius: 12px;
  padding: 11px 14px;
  cursor: pointer;
  margin-bottom: 16px;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.iqd-chat:hover { border-color: #b9cef7; background: #eef3fd; }
.iqd-chat__icon {
  position: relative;
  flex: none;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #e1ecff;
  color: #2353b8;
  display: flex;
  align-items: center;
  justify-content: center;
}
.iqd-chat__online {
  position: absolute;
  right: -2px;
  bottom: -2px;
  width: 11px;
  height: 11px;
  border-radius: 50%;
  background: #2c9d5a;
  box-shadow: 0 0 0 2px #f4f7fd;
}
.iqd-chat__text { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.iqd-chat__title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13.5px;
  font-weight: 700;
  color: #1a1814;
}
.iqd-chat__unread {
  font-size: 10.5px;
  font-weight: 700;
  color: #fff;
  background: #2353b8;
  border-radius: 999px;
  padding: 1px 8px;
}
.iqd-chat__meta {
  font-size: 12px;
  color: #6a7486;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.iqd-chat__go {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12.5px;
  font-weight: 700;
  color: #2353b8;
}
.iqd-chat__go .hero-icon { color: inherit; }

.iqd-cards {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 16px;
}

/* property package */
.iqd-pkg { display: flex; flex-direction: column; gap: 8px; }
.iqd-seclabel {
  font-size: 10px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #a39a8c;
  font-weight: 600;
  margin: 2px 0 1px;
}

/* message */
.iqd-message {
  background: #faf8f4;
  border: 1px solid #ece7de;
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 18px;
}
.iqd-message__label {
  font-size: 10px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #a39a8c;
  font-weight: 600;
  margin-bottom: 6px;
}
.iqd-message__text {
  font-size: 13.5px;
  color: #1a1814;
  line-height: 1.55;
  white-space: pre-wrap;
}

/* comments */
.iqd-comments__title {
  font-size: 10px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #a39a8c;
  font-weight: 600;
  margin-bottom: 11px;
}
.iqd-comments__empty {
  font-size: 13px;
  color: #a39a8c;
  border: 1px dashed #dcd4c6;
  border-radius: 12px;
  padding: 18px;
  text-align: center;
}
.iqd-bubble {
  background: #fff;
  border: 1px solid #ece7de;
  border-radius: 12px;
  padding: 11px 14px;
  margin-bottom: 9px;
}
.iqd-bubble__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 4px;
}
.iqd-bubble--client {
  background: #fdf6ee;
  border-color: #ecd9bd;
}
.iqd-bubble__author { font-size: 12px; font-weight: 600; color: #1a1814; }
.iqd-bubble__tag {
  display: inline-block;
  margin-left: 7px;
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #1d6b3a;
  background: #e9f4ec;
  border-radius: 999px;
  padding: 2px 8px;
  vertical-align: 1px;
}
.iqd-bubble__tag--client {
  color: #92400e;
  background: #fdeed3;
}
.iqd-bubble__date { font-size: 11.5px; color: #a39a8c; }
.iqd-bubble__text {
  font-size: 13.5px;
  color: #1a1814;
  line-height: 1.5;
  white-space: pre-wrap;
}

/* composer */
.iqd-composerwrap {
  border-top: 1px solid #ece7de;
  background: #fff;
  padding: 10px 22px 18px;
}
.iqd-composerhint {
  font-size: 11.5px;
  color: #a39a8c;
  padding: 4px 2px 9px;
}
.iqd-composerhint--client {
  color: #92400e;
  font-weight: 600;
}
.iqd-composer {
  display: flex;
  align-items: flex-end;
  gap: 10px;
}
.iqd-composer--client .iqd-textarea >>> fieldset {
  border-color: #b52118 !important;
}
.iqd-textarea { flex: 1; }
.iqd-send {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  flex: none;
  border-radius: 11px;
  border: none;
  background: #b52118;
  color: #fff;
  cursor: pointer;
  transition: background 0.15s ease, opacity 0.15s ease;
}
.iqd-send:hover:not(:disabled) { background: #9c1c14; }
.iqd-send:disabled { opacity: 0.4; cursor: default; }

/* confirm dialog */
.iqd-confirm { background: #fff; border-radius: 14px; overflow: hidden; }
.iqd-confirm__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px 14px;
}
.iqd-confirm__title { font-size: 16px; font-weight: 600; color: #1a1814; }
.iqd-confirm__body {
  padding: 18px 20px;
  font-size: 14px;
  color: #56504a;
  line-height: 1.55;
}
.iqd-confirm__body strong { color: #b52118; }
.iqd-confirm__actions {
  display: flex;
  justify-content: flex-end;
  gap: 9px;
  padding: 0 20px 20px;
}

/* warm outlined fields */
::v-deep .v-text-field--outlined fieldset {
  border-radius: 10px;
  border-color: #ece7de;
}
::v-deep .v-text-field--outlined:hover fieldset { border-color: #dcd4c6; }
::v-deep .v-text-field--outlined.v-input--is-focused fieldset { border-color: #b52118; }
::v-deep .v-label { color: #a39a8c; }
::v-deep .v-label.v-label--active { color: #756f64; }
</style>
